import { useState } from "react";
import { Alert, Box, Typography, Button } from "@mui/material";
import { useRecordContext, useNotify, useRefresh } from "react-admin";
import { EditableGrid, EditorField } from "@/components/common/EditableGrid";
import { TabContent } from "@/components/common/PageLayout";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { AlertType, ResourceName } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { generateDarkBranding, isValidHexColor } from "@/utils/colorUtils";
import { pickBranding, syncBrandingCache } from "@/utils/brandingSync";
import { putPartnerBranding } from "@/utils/partnerBranding";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { BrandingPreview } from "./BrandingPreview";
import { LogoUploadSection } from "./LogoUploadSection";

export const PartnerBrandingTab = () => {
  const { canEdit } = useResourcePermissions(ResourceName.PARTNERS);
  const record = useRecordContext();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [isLoading, setIsLoading] = useState(false);

  // Canonical dedicated branding column, treating empty `{}` as absent (#4116).
  const currentBranding = pickBranding(record);
  const hasPrimary = isValidHexColor(currentBranding?.brand_primary);
  const hasSecondary = isValidHexColor(currentBranding?.brand_secondary);
  const canGenerateDark = hasPrimary && hasSecondary;
  const hasDarkColors =
    isValidHexColor(currentBranding?.brand_primary_dark) && isValidHexColor(currentBranding?.brand_secondary_dark);

  const handleGenerateDark = () => {
    if (!record || !canGenerateDark || !currentBranding) return;

    const darkColors = generateDarkBranding(currentBranding.brand_primary!, currentBranding.brand_secondary!);
    const merged = { ...currentBranding, ...darkColors };

    setIsLoading(true);
    void putPartnerBranding(String(record.id), merged)
      .then(() => {
        notify("Dark mode colors generated", { type: "success" });
        // Update the active-partner theme cache immediately, then refetch the record.
        syncBrandingCache({ ...record, branding: merged });
        refresh();
      })
      .catch((error) => notifyError(error))
      .finally(() => setIsLoading(false));
  };
  return (
    <TabContent>
      {!canEdit && (
        <Box mb={2}>
          <Alert severity={AlertType.INFO}>
            Contact Aegle Capital if you need to make changes to your partner branding.
          </Alert>
        </Box>
      )}

      <ShowAccordionSection label="Colors" defaultExpanded>
        <EditableGrid fields={brandingFields} recordType={ResourceName.PARTNERS} />

        <Box sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Typography variant="h5">Dark Mode Colors</Typography>
            {canEdit && canGenerateDark && (
              <Button
                size="small"
                startIcon={<AutoFixHighIcon />}
                onClick={handleGenerateDark}
                disabled={isLoading}
                sx={{ textTransform: "none" }}
              >
                {hasDarkColors ? "Regenerate from light colors" : "Auto-generate from light colors"}
              </Button>
            )}
          </Box>
          {!canGenerateDark && canEdit && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Set primary and secondary colors above to auto-generate dark mode variants.
            </Typography>
          )}
          <EditableGrid fields={darkBrandingFields} recordType={ResourceName.PARTNERS} />
        </Box>
      </ShowAccordionSection>

      <ShowAccordionSection label="Logo" defaultExpanded>
        <LogoUploadSection />
      </ShowAccordionSection>

      <ShowAccordionSection label="Preview" defaultExpanded>
        <BrandingPreview />
      </ShowAccordionSection>
    </TabContent>
  );
};

const brandingFields: EditorField[] = [
  { source: "branding.brand_primary", type: "color" },
  { source: "branding.brand_secondary", type: "color" },
];

const darkBrandingFields: EditorField[] = [
  { source: "branding.brand_primary_dark", type: "color" },
  { source: "branding.brand_secondary_dark", type: "color" },
];
